import { useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowRight, Download, RotateCcw, Search } from 'lucide-react'

import { Link } from '@/lib/router'
import { api } from '@/lib/api/client'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip, ChipGroup } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Kpi } from '@/components/ui/kpi'
import { Skeleton, SkeletonRows } from '@/components/ui/skeleton'

import { REGULAR_BUCKET_LABEL, bucketLabel, date, dpd, humanizeCode, inr, num, priorityColor } from '@/lib/format'
import { useDebouncedValue } from '@/lib/useDebouncedValue'

import {
  joinFilter, useCaseTotals, useCases, usePtpStats,
  type CaseRow, type CaseSort, type CollectionFilter, type PriorityBand, type PtpFilter,
} from './api'
import { useAgents } from '@/features/allocations/api'
import { useMe } from '@/features/auth/api'
import { navPermissionCheck } from '@/components/layout/navVisibility'
import { FreshnessBadge } from '@/features/freshness/FreshnessBadge'
import { CaseDetailDrawer } from './CaseDetailDrawer'
import { parseListParam, toggleIn } from './filters'
import { CASE_PAGE_SIZE, pageCountFor, useClampPage } from './paging'
import { PageSummary, PagerButtons } from './Pager'

type BucketKey = 'CURRENT' | 'BUCKET_1_30' | 'BUCKET_31_60' | 'BUCKET_61_90' | 'BUCKET_90_PLUS'

const PRIORITY_CHIPS: { key: PriorityBand; label: string }[] = [
  { key: 'P1', label: 'P1' },
  { key: 'P2', label: 'P2' },
  { key: 'P3', label: 'P3' },
  { key: 'P4', label: 'P4' },
]

const PTP_CHIPS: { key: PtpFilter; label: string; stat: 'dueToday' | 'dueIn3d' | 'dueSoon' | 'overdue' }[] = [
  { key: 'DUE_TODAY', label: 'Due today', stat: 'dueToday' },
  { key: 'DUE_3D',    label: 'Due ≤ 3d',  stat: 'dueIn3d' },
  { key: 'DUE_SOON',  label: 'Due ≤ 7d',  stat: 'dueSoon' },
  { key: 'OVERDUE',   label: 'Overdue',   stat: 'overdue' },
]

const COLLECTION_CHIPS: { key: CollectionFilter | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'PAID', label: 'Paid' },
  { key: 'UNPAID', label: 'Unpaid' },
]

/** "Due" is the demand (overdue + charges) — the figure an agent quotes — never POS. */
const SORTS: { key: CaseSort; label: string; subtitle: string }[] = [
  { key: 'priority', label: 'Priority',        subtitle: 'sorted by priority score' },
  { key: 'due',      label: 'Due, highest',    subtitle: 'sorted by amount due, highest first' },
  { key: 'dpd',      label: 'DPD, oldest',     subtitle: 'sorted by days past due, oldest first' },
  { key: 'ptp',      label: 'Next promise',    subtitle: 'sorted by the soonest open promise' },
]

const BUCKET_CHIPS: { key: BucketKey; label: string }[] = [
  { key: 'CURRENT',        label: REGULAR_BUCKET_LABEL },
  { key: 'BUCKET_1_30',    label: '1–30' },
  { key: 'BUCKET_31_60',   label: '31–60' },
  { key: 'BUCKET_61_90',   label: '61–90' },
  { key: 'BUCKET_90_PLUS', label: '90+' },
]

const BUCKET_KEYS: BucketKey[] = BUCKET_CHIPS.map((c) => c.key)
const PTP_KEYS: PtpFilter[] = [...PTP_CHIPS.map((c) => c.key), 'OPEN']
const PRIORITY_KEYS: PriorityBand[] = PRIORITY_CHIPS.map((c) => c.key)

export function CasesPage() {
  const navigate = useNavigate()
  const { data: me } = useMe()
  const canUpload = navPermissionCheck(me)('INGESTION_UPLOAD')
  // The drawer's open state is mirrored to the URL via the optional
  // `case` search param. Sharing /cases?case=<id> opens the right case
  // with the queue already filtered the way the sharer left it. The
  // `bucket`, `ptp` and `priority` search params (comma-separated for
  // several) are accepted too so drill-downs from the dashboard land with
  // the filter pre-applied.
  const search = useSearch({ strict: false }) as {
    case?: string; bucket?: string; ptp?: string; mine?: string
    priority?: string; sort?: CaseSort; collection?: string
  }
  const drawerCaseId = search.case ?? null
  const initialScope: 'all' | 'mine' = search.mine === 'true' ? 'mine' : 'all'

  const [scope, setScope] = useState<'all' | 'mine'>(initialScope)
  // Each chip group is multi-select: empty means "All", several values
  // match any of them, and different groups narrow each other.
  const [ptps, setPtps] = useState<PtpFilter[]>(() => parseListParam(search.ptp, PTP_KEYS))
  const [buckets, setBuckets] = useState<BucketKey[]>(() => parseListParam(search.bucket, BUCKET_KEYS))
  const [priorities, setPriorities] = useState<PriorityBand[]>(() => parseListParam(search.priority, PRIORITY_KEYS))
  const [sort, setSort] = useState<CaseSort>(search.sort ?? 'priority')
  // From the dashboard's Paid / Unpaid tiles. Anything else is ignored rather
  // than sent, since the API rejects an unknown value with a 400.
  const [collection, setCollection] = useState<CollectionFilter | 'ALL'>(
    search.collection === 'PAID' || search.collection === 'UNPAID' ? search.collection : 'ALL',
  )
  const [page, setPage] = useState(0)
  // Filter by collection agent. The list comes from the allocations agent
  // list (already scoped to the caller); without CASE_ALLOCATE it 403s and
  // the filter simply isn't offered.
  const [agent, setAgent] = useState('')
  const { data: agents } = useAgents('FIELD')
  const PAGE_SIZE = CASE_PAGE_SIZE
  const [q, setQ] = useState('')
  const debouncedQ = useDebouncedValue(q.trim(), 300)
  // Search and every filter run on the SERVER (BUG-003): filtering the
  // fetched page client-side silently dropped every match on other pages.
  // One filter set feeds both the page and the totals so they can't drift.
  const filters = {
    mine: scope === 'mine',
    ...(ptps.length > 0 ? { ptp: ptps } : {}),
    ...(buckets.length > 0 ? { bucket: buckets } : {}),
    ...(priorities.length > 0 ? { priority: priorities } : {}),
    ...(collection !== 'ALL' ? { collection } : {}),
    ...(agent && scope === 'all' ? { agent, slot: 'FIELD' as const } : {}),
    ...(debouncedQ ? { q: debouncedQ } : {}),
  }
  const { data: cases, isPending, isError } = useCases(
    { ...filters, sort, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
    { keepPrevious: true },
  )
  // The KPI cards and the "of N" cover every matching case, not this page.
  const { data: totals, isPending: totalsPending } = useCaseTotals(filters)
  const rows = cases ?? []
  const total = totals?.total ?? null
  const { data: ptpStats } = usePtpStats()
  const [exporting, setExporting] = useState(false)

  // A new search starts from the first page, as every filter chip does.
  useEffect(() => { setPage(0) }, [debouncedQ])
  useClampPage(page, setPage, pageCountFor(total))

  const filtersActive = Boolean(debouncedQ) || buckets.length > 0 || ptps.length > 0
    || priorities.length > 0 || collection !== 'ALL' || (agent !== '' && scope === 'all')
  // What "Clear filters" has to undo: the filters, plus a non-default scope or sort.
  const resettable = filtersActive || q !== '' || agent !== '' || scope !== 'all' || sort !== 'priority'

  // Back to the default queue in one click. The URL's filter params are
  // dropped too, or a reload would bring a dashboard drill-down back.
  function clearFilters() {
    setBuckets([])
    setPtps([])
    setPriorities([])
    setCollection('ALL')
    setAgent('')
    setQ('')
    setSort('priority')
    setScope('all')
    setPage(0)
    navigate({ to: '/cases', search: (drawerCaseId ? { case: drawerCaseId } : {}) as never })
  }

  // Server-side XLSX export (BUG-006) — covers the FULL filtered dataset,
  // not just the fetched page. Auth rides the axios interceptor, so this
  // can't be a plain <a href>.
  async function exportXlsx() {
    setExporting(true)
    try {
      const params: Record<string, string> = { status: 'OPEN' }
      if (buckets.length > 0) params.bucket = joinFilter(buckets)
      const res = await api.get<Blob>('/v1/collections/export.xlsx', {
        params,
        responseType: 'blob',
      })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `collections-export-${new Date().toISOString().slice(0, 10)}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  function openCase(caseId: string) {
    navigate({ to: '/cases', search: { ...search, case: caseId } as never })
  }
  function closeDrawer() {
    const next = { ...search }
    delete next.case
    navigate({ to: '/cases', search: next as never })
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">Operations</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-navy-900">Cases queue</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Open delinquencies, {SORTS.find((x) => x.key === sort)?.subtitle ?? 'sorted by priority score'}.
            Auto-refreshes every 5 seconds.
          </p>
          <FreshnessBadge className="mt-2" />
        </div>
        <Button variant="outline" onClick={exportXlsx} disabled={exporting}>
          <Download className="h-3.5 w-3.5" />
          {exporting ? 'Exporting…' : 'Export XLSX'}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="In view" value={num(totals?.total ?? 0)} hint={buckets.length === 0 ? 'All buckets' : `Bucket ${buckets.map(bucketLabel).join(', ')}`} loading={totalsPending} deltaSense="neutral" />
        <Kpi label="Outstanding (in view)" value={inr(totals?.outstanding ?? 0)} hint="Open outstanding only" loading={totalsPending} deltaSense="lower-is-better" />
        <Kpi label="P1 priority" value={num(totals?.p1Count ?? 0)} hint="Highest urgency" loading={totalsPending} deltaSense="lower-is-better" />
        <Kpi label="P2 priority" value={num(totals?.p2Count ?? 0)} hint="Work after P1" loading={totalsPending} deltaSense="lower-is-better" />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Queue</CardTitle>
              <CardDescription>Click a row to open the case detail.</CardDescription>
            </div>
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search loan no, customer, ref, mobile…"
                aria-label="Search all cases"
                style={{ paddingLeft: '2rem' }}
              />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-2xs uppercase tracking-wider text-navy-600">Scope</span>
            <ChipGroup>
              <Chip active={scope === 'all'} onClick={() => { setScope('all'); setPage(0) }}>All cases</Chip>
              <Chip active={scope === 'mine'} onClick={() => { setScope('mine'); setPage(0) }}>My queue</Chip>
            </ChipGroup>
            <span className="mx-2 hidden h-4 w-px bg-slate-200 sm:inline-block" />
            <span className="text-2xs uppercase tracking-wider text-navy-600">Bucket</span>
            <ChipGroup>
              {/* No per-chip counts: they were computed over the fetched page
                  only and lied whenever the queue exceeded one page. */}
              <Chip active={buckets.length === 0} onClick={() => { setBuckets([]); setPage(0) }}>All buckets</Chip>
              {BUCKET_CHIPS.map((c) => (
                <Chip
                  key={c.key}
                  active={buckets.includes(c.key)}
                  onClick={() => { setBuckets((b) => toggleIn(b, c.key, BUCKET_KEYS)); setPage(0) }}
                >
                  {c.label}
                </Chip>
              ))}
            </ChipGroup>
            <span className="mx-2 hidden h-4 w-px bg-slate-200 sm:inline-block" />
            <span className="text-2xs uppercase tracking-wider text-navy-600">PTP</span>
            <ChipGroup>
              <Chip active={ptps.length === 0} onClick={() => { setPtps([]); setPage(0) }}>All</Chip>
              {PTP_CHIPS.map((c) => (
                <Chip
                  key={c.key}
                  active={ptps.includes(c.key)}
                  {...(ptpStats?.[c.stat] !== undefined ? { count: ptpStats[c.stat] } : {})}
                  onClick={() => { setPtps((p) => toggleIn(p, c.key, PTP_KEYS)); setPage(0) }}
                >
                  {c.label}
                </Chip>
              ))}
            </ChipGroup>
            <span className="mx-2 hidden h-4 w-px bg-slate-200 sm:inline-block" />
            <span className="text-2xs uppercase tracking-wider text-navy-600">Priority</span>
            <ChipGroup>
              <Chip active={priorities.length === 0} onClick={() => { setPriorities([]); setPage(0) }}>All</Chip>
              {PRIORITY_CHIPS.map((c) => (
                <Chip key={c.key} active={priorities.includes(c.key)}
                      onClick={() => { setPriorities((p) => toggleIn(p, c.key, PRIORITY_KEYS)); setPage(0) }}>
                  {c.label}
                </Chip>
              ))}
            </ChipGroup>
            <span className="mx-2 hidden h-4 w-px bg-slate-200 sm:inline-block" />
            {/* Paid = anything collected since the dues file, as on the dashboard and the agent app. */}
            <span className="text-2xs uppercase tracking-wider text-navy-600">Payment</span>
            <ChipGroup>
              {COLLECTION_CHIPS.map((c) => (
                <Chip key={c.key} active={collection === c.key}
                      onClick={() => { setCollection(c.key); setPage(0) }}>
                  {c.label}
                </Chip>
              ))}
            </ChipGroup>
            <span className="mx-2 hidden h-4 w-px bg-slate-200 sm:inline-block" />
            <label htmlFor="case-sort" className="text-2xs uppercase tracking-wider text-navy-600">Sort</label>
            <select
              id="case-sort"
              value={sort}
              onChange={(e) => { setSort(e.target.value as CaseSort); setPage(0) }}
              className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs text-navy-900"
            >
              {SORTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            {scope === 'all' && agents && agents.length > 0 && (
              <>
                <span className="mx-2 hidden h-4 w-px bg-slate-200 sm:inline-block" />
                <label htmlFor="case-agent" className="text-2xs uppercase tracking-wider text-navy-600">Agent</label>
                <select
                  id="case-agent"
                  value={agent}
                  onChange={(e) => { setAgent(e.target.value); setPage(0) }}
                  className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs text-navy-900"
                >
                  <option value="">All agents</option>
                  {agents.map((a) => <option key={a.userId} value={a.userId}>{a.fullName} · {a.empId}</option>)}
                </select>
              </>
            )}
            {resettable && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto">
                <RotateCcw className="h-3.5 w-3.5" />
                Clear filters
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="px-0 pb-0">
          {isPending && <SkeletonRows rows={6} cols={10} />}
          {isError && (
            <div className="p-6 text-sm text-negative">
              Couldn't load cases. The first one appears after a DPD_DUES batch reconciles.
            </div>
          )}
          {cases && rows.length === 0 && !isPending && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-navy-900">
                {filtersActive ? 'No cases match this filter' : 'No open cases in your view'}
              </p>
              <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                {!filtersActive ? (
                  <>
                    A manager's list shows the cases allocated to their agents — hand some out from{' '}
                    <Link to="/allocations" className="font-medium text-navy-700 hover:underline">Allocations</Link>.
                    On a new tenant, cases appear once a{' '}
                    {canUpload ? (
                      <Link to="/ingest" className="font-medium text-navy-700 hover:underline">DPD_DUES</Link>
                    ) : (
                      'DPD_DUES'
                    )}{' '}
                    batch reconciles.
                  </>
                ) : (
                  <>
                    Change the search or filters, or{' '}
                    <button type="button" onClick={clearFilters} className="font-medium text-navy-700 hover:underline">
                      clear filters
                    </button>.
                  </>
                )}
              </p>
            </div>
          )}

          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                  <tr>
                    <th className="px-5 py-2 font-medium">Priority</th>
                    <th className="px-3 py-2 font-medium">Loan account</th>
                    <th className="px-3 py-2 font-medium">Customer</th>
                    <th className="px-3 py-2 font-medium">Collection agent</th>
                    <th className="px-3 py-2 font-medium">Product</th>
                    <th className="px-3 py-2 text-right font-medium">DPD</th>
                    <th className="px-3 py-2 font-medium">Bucket</th>
                    <th className="px-3 py-2 text-right font-medium" title="Overdue + charges — what the borrower is asked to pay">Due</th>
                    <th className="px-3 py-2 text-right font-medium" title="Principal left on the whole loan — portfolio data">Outstanding (POS)</th>
                    <th className="px-3 py-2 font-medium">As of</th>
                    <th className="px-5 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => <Row key={c.caseId} c={c} onOpen={openCase} />)}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        <CardFooter>
          <span className="text-2xs uppercase tracking-wider text-muted-foreground">
            <PageSummary page={page} rowsOnPage={rows.length} total={total} />
          </span>
          <span className="inline-flex items-center gap-3">
            <PagerButtons page={page} setPage={setPage} rowsOnPage={rows.length} total={total} />
            <span className="inline-flex items-center gap-2 text-2xs uppercase tracking-wider text-muted-foreground">
              <Skeleton className="h-1.5 w-1.5 rounded-full bg-navy-500" />
              Live · 5s
            </span>
          </span>
        </CardFooter>
      </Card>

      <CaseDetailDrawer caseId={drawerCaseId} onClose={closeDrawer} />
    </div>
  )
}

function Row({ c, onOpen }: { c: CaseRow; onOpen: (caseId: string) => void }) {
  const pc = priorityColor(c.priorityBand)
  return (
    <tr
      onClick={() => onOpen(c.caseId)}
      className="cursor-pointer border-b border-slate-200 last:border-0 transition-colors duration-fluid ease-fluid hover:bg-navy-50"
    >
      <td className="px-5 py-2">
        <span
          className="inline-flex items-center rounded-md px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider"
          style={{ background: pc.bg, color: pc.fg }}
        >
          {c.priorityBand ?? '—'}
        </span>
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-navy-900">{c.loanAccountNo}</span>
          {c.dncFlag && (
            <span className="inline-flex items-center rounded-sm border border-negative/35 bg-negative-soft px-1 py-0 text-[9px] font-semibold uppercase tracking-wider text-negative">
              DNC
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-navy-900">{c.customerName}</td>
      <td className="px-3 py-2">
        {c.fieldAgentName
          ? <span className="text-navy-900">{c.fieldAgentName} <span className="font-mono text-2xs text-muted-foreground">{c.fieldAgentEmpId}</span></span>
          : <span className="text-muted-foreground" title="No field agent allocated">—</span>}
      </td>
      <td className="px-3 py-2 text-muted-foreground">{humanizeCode(c.productType)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-navy-900">{dpd(c.currentDpd)}</td>
      <td className="px-3 py-2">
        <Badge tone={bucketTone(c.currentBucket)}>{bucketLabel(c.currentBucket)}</Badge>
      </td>
      <td className="px-3 py-2 text-right tabular-nums font-medium text-navy-900">{inr(c.currentOverdue)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{inr(c.currentOutstanding)}</td>
      <td className="px-3 py-2 text-muted-foreground">{date(c.asOfDate)}</td>
      <td className="px-5 py-2 text-right">
        <span
          className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wider text-navy-700"
          aria-hidden
        >
          Open <ArrowRight className="h-3 w-3" />
        </span>
      </td>
    </tr>
  )
}

function bucketTone(b: string | null | undefined): 'positive' | 'navy' | 'warning' | 'negative' | 'slate' {
  switch (b) {
    case 'CURRENT':         return 'positive'
    case 'BUCKET_1_30':     return 'navy'
    case 'BUCKET_31_60':    return 'warning'
    case 'BUCKET_61_90':    return 'warning'
    case 'BUCKET_90_PLUS':  return 'negative'
    default:                return 'slate'
  }
}
